import { Info, Wallet } from 'lucide-react';
import type { BookingPaymentBreakdown } from '@/features/booking/types';

const rupees = (v: string) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * What a vendor is actually paid for a booking.
 *
 * On a service the platform's cut is DEDUCTED — the customer pays the price that was
 * quoted and the vendor receives less. That is the opposite of a product, where the
 * fee is added on top of the vendor's price. A vendor shown only the booking price
 * would reasonably expect all of it, so the three numbers appear together with the
 * take-home emphasised.
 *
 * The figures come from what was recorded when the booking was priced, not from
 * today's rate, so they keep matching what was settled after a rate change.
 */
export function BookingPaymentBreakdownPanel({
  breakdown,
  payment_status,
}: {
  breakdown: BookingPaymentBreakdown | undefined;
  payment_status?: string;
}) {
  if (!breakdown) return null;

  const paid = payment_status === 'CAPTURED';

  return (
    <div
      className="rounded-input border border-border bg-secondary/30 p-3"
      data-testid="booking-payment-breakdown"
    >
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Wallet className="h-3.5 w-3.5" /> Payment
        {payment_status && <span className="ml-auto normal-case">{paid ? 'Paid' : payment_status.toLowerCase()}</span>}
      </p>

      <dl className="flex flex-col gap-1 text-sm tabular-nums">
        <div className="flex items-baseline justify-between">
          <dt className="text-muted-foreground">Customer paid</dt>
          <dd data-testid="breakdown-customer-price">{rupees(breakdown.customer_price_inr)}</dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-muted-foreground">
            Platform fee
            {breakdown.rate_pct ? <span className="ml-1 text-xs">({breakdown.rate_pct}%)</span> : null}
          </dt>
          <dd className="text-muted-foreground" data-testid="breakdown-platform-fee">
            −{rupees(breakdown.platform_fee_inr)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between border-t border-border pt-1">
          <dt className="font-semibold">You take home</dt>
          <dd className="font-heading text-base font-bold text-primary" data-testid="breakdown-payout">
            {rupees(breakdown.vendor_payout_inr)}
          </dd>
        </div>
      </dl>

      {breakdown.is_estimate && (
        /*
         * Predates the fee being recorded on bookings, so this is derived rather
         * than settled. Saying so beats implying a precision we don't have.
         */
        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-muted-foreground" data-testid="breakdown-estimate">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          This booking predates platform fees, so nothing was deducted.
        </p>
      )}
    </div>
  );
}
